/* eslint-disable react/forbid-prop-types */
import React, {
  useEffect, useMemo, useRef, useState
} from 'react'
import PropTypes from 'prop-types'
import { Autocomplete } from '@telus/koodo-ui-kit'

// Smallest usable overlay; below this the list scrolls internally rather than
// shrinking to an unreadable sliver when the input sits low in the viewport.
const MIN_DROPDOWN_HEIGHT = 160
// Breathing room kept between the bottom of the dropdown and the viewport edge.
const VIEWPORT_GAP = 16

const DatalistV2 = ({
  value = '',
  title,
  placeholder,
  items,
  onChange,
  onBlur,
  error,
  disabled = false,
  loading = false,
  id = null,
  onFocus,
  maxDropdownHeight = 280
}) => {
  const wrapperRef = useRef(null)
  const randomInputName = useMemo(() => `dl-${Math.random().toString(36).slice(2)}`, [])
  const [dropdownHeight, setDropdownHeight] = useState(maxDropdownHeight)

  useEffect(() => {
    const input = wrapperRef.current?.querySelector('input')
    if (!input) return undefined

    const enforce = () => {
      if (input.getAttribute('autocomplete') !== 'new-password') {
        input.setAttribute('autocomplete', 'new-password')
      }
      if (input.getAttribute('name') !== randomInputName) {
        input.setAttribute('name', randomInputName)
      }
    }
    enforce()

    const observer = new MutationObserver(enforce)
    observer.observe(input, { attributes: true, attributeFilter: ['autocomplete', 'name'] })
    return () => observer.disconnect()
  }, [])

  // The Autocomplete moves the keyboard highlight by pointing the input's
  // aria-activedescendant at the active option, but it never scrolls that
  // option into view. Once the list is capped (maxDropdownHeight) and scrolls
  // internally, arrow-key navigation runs off-screen. Mirror the active option
  // into view whenever aria-activedescendant changes so arrow up/down keeps the
  // highlighted item visible. block:'nearest' scrolls only the list, not the page.
  useEffect(() => {
    const input = wrapperRef.current?.querySelector('input')
    if (!input) return undefined

    const scrollActiveOptionIntoView = () => {
      const activeId = input.getAttribute('aria-activedescendant')
      if (!activeId) return
      document.getElementById(activeId)?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }

    const observer = new MutationObserver(scrollActiveOptionIntoView)
    observer.observe(input, { attributes: true, attributeFilter: ['aria-activedescendant'] })
    return () => observer.disconnect()
  }, [])

  const options = useMemo(() => {
    return items.map((item) => ({
      id: item.value,
      label: item.name
    }))
  }, [items])

  const handleChange = (text) => {
    onChange({ target: { value: text } })
  }

  const handleBlur = (event) => {
    if (onBlur) onBlur(event)
  }

  // Measure the space left below the input within the viewport and cap the
  // overlay to it (never above the configured max). This keeps the portaled
  // dropdown fully on-screen so opening it doesn't grow the document and
  // trigger an auto-scroll.
  const updateDropdownHeight = () => {
    const input = wrapperRef.current?.querySelector('input')
    if (!input) return
    const { bottom } = input.getBoundingClientRect()
    const available = window.innerHeight - bottom - VIEWPORT_GAP
    const next = Math.min(maxDropdownHeight, Math.max(MIN_DROPDOWN_HEIGHT, available))
    setDropdownHeight(next)
  }

  const handleFocus = () => {
    updateDropdownHeight()
    if (onFocus) onFocus()
  }

  return (
    <div ref={wrapperRef} className={`relative z-50 ${disabled ? 'cursor-not-allowed' : ''}`}>
      <div className={disabled ? 'pointer-events-none' : ''}>
        <Autocomplete
          id={id}
          label={title}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          options={options}
          placeholder={placeholder}
          isLoading={loading}
          disabled={disabled}
          feedback={error}
          validation={error ? 'error' : undefined}
          value={value}
          maxSuggestions={options.length}
          // Cap the overlay height so the list scrolls internally instead of
          // rendering all items at full height. The dropdown is portaled to
          // <body> as an absolutely positioned element, so a tall overlay near
          // the bottom of the page grows the document height and triggers an
          // auto-scroll on open. The cap is computed from the space left below
          // the input on focus so the list always fits within the viewport.
          maxDropdownHeight={dropdownHeight}
        />
      </div>
    </div>
  )
}

DatalistV2.propTypes = {
  title: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onFocus: PropTypes.func,
  id: PropTypes.string,
  maxDropdownHeight: PropTypes.number
}

export default DatalistV2